import {
  GoogleAuthProvider,
  SAMLAuthProvider,
  OAuthProvider,
  signInWithRedirect,
  getRedirectResult,
  type AuthProvider,
  type AuthCredential,
  type UserCredential,
} from 'firebase/auth';
import { getBridgeAuth } from './firebase';
import { getBridgeConfig } from './config';
import { isAllowedOrigin } from './allowlist';
import { parseAndValidateParams, type BridgeParams } from './params';
import { sendToParent, type BridgeMessage } from './post-message';

const SESSION_KEY = 'marquee-auth-bridge-context';

interface BridgeContext {
  parentOrigin: string;
  tenantId: string | null;
}

function buildProvider(p: BridgeParams): AuthProvider {
  if (p.provider === 'google') {
    return new GoogleAuthProvider();
  }
  if (p.provider === 'saml') {
    return new SAMLAuthProvider(p.providerId!);
  }
  return new OAuthProvider(p.providerId!);
}

function readStoredContext(): BridgeContext | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.parentOrigin !== 'string') return null;
    return {
      parentOrigin: parsed.parentOrigin,
      tenantId: typeof parsed.tenantId === 'string' ? parsed.tenantId : null,
    };
  } catch {
    return null;
  }
}

function clearStoredContext(): void {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
}

function failClosed(parentOrigin: string | null): void {
  if (parentOrigin) {
    try {
      sendToParent({ type: 'auth-bridge:error', error: 'auth_failed' }, parentOrigin);
    } catch {
      /* nothing to do */
    }
  }
  clearStoredContext();
  setTimeout(() => window.close(), 100);
}

async function deliverRedirectResult(): Promise<boolean> {
  const auth = getBridgeAuth();
  let result;
  try {
    result = await getRedirectResult(auth);
  } catch (err: any) {
    // eslint-disable-next-line no-console
    console.error('[auth-bridge] getRedirectResult failed', err);
    const ctx = readStoredContext();
    failClosed(ctx?.parentOrigin ?? null);
    return true;
  }
  if (!result) return false;

  const ctx = readStoredContext();
  if (!ctx) {
    // We have a credential but no stored parent origin — can't safely deliver.
    // Sign out the just-created session to avoid stranding it in IndexedDB.
    try {
      await auth.signOut();
    } catch {
      /* ignore */
    }
    setTimeout(() => window.close(), 100);
    return true;
  }

  const cfg = getBridgeConfig();
  if (!isAllowedOrigin(ctx.parentOrigin, cfg.env)) {
    try {
      await auth.signOut();
    } catch {
      /* ignore */
    }
    clearStoredContext();
    setTimeout(() => window.close(), 100);
    return true;
  }

  // Extract the OAuth credential from the result. This is the provider's
  // (Google's / SAML IdP's) credential — NOT a Firebase ID token. The
  // parent at bobba's origin will reconstruct it via credentialFromJSON()
  // and call signInWithCredential() to mint its own Firebase session.
  // Sending result.user.getIdToken() instead would send Firebase's ID token
  // bound to the bridge origin's session — useless to the parent and
  // rejected by signInWithIdp with HTTP 400.
  const oauthCredential = extractOAuthCredential(result);
  if (!oauthCredential) {
    // eslint-disable-next-line no-console
    console.error('[auth-bridge] no OAuth credential in redirect result', {
      providerId: result.providerId,
    });
    failClosed(ctx.parentOrigin);
    return true;
  }

  const credentialJson = oauthCredential.toJSON() as Record<string, unknown>;
  const providerId = result.providerId ?? 'unknown';
  const message: BridgeMessage = {
    type: 'auth-bridge:success',
    credentialJson,
    providerId,
  };

  try {
    sendToParent(message, ctx.parentOrigin);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[auth-bridge] postMessage to parent failed', err);
    clearStoredContext();
    setTimeout(() => window.close(), 100);
    return true;
  }

  // Sign the bridge out so we don't leave a stranded Firebase session at
  // auth.dev.ondeo.store's IndexedDB. The parent now has its own session
  // via signInWithCredential().
  try {
    await auth.signOut();
  } catch {
    /* ignore */
  }
  clearStoredContext();
  setTimeout(() => window.close(), 100);
  return true;
}

function extractOAuthCredential(result: UserCredential): AuthCredential | null {
  const providerId = result.providerId ?? '';
  if (providerId === 'google.com') {
    return GoogleAuthProvider.credentialFromResult(result);
  }
  if (providerId.startsWith('saml.')) {
    return SAMLAuthProvider.credentialFromResult(result);
  }
  if (providerId.startsWith('oidc.')) {
    return OAuthProvider.credentialFromResult(result);
  }
  return null;
}

async function runBridge(): Promise<void> {
  // Anti-clickjacking: refuse to run inside an iframe.
  if (window.top !== window.self) {
    document.body.textContent = 'Forbidden: embedded';
    return;
  }
  // Refuse to run if not opened as a popup.
  if (!window.opener) {
    document.body.textContent = 'Forbidden: must be opened as popup';
    return;
  }

  // First: check whether we're returning from a redirect. If so, deliver the
  // credential and exit. This branch runs on the SECOND visit to the bridge —
  // after Google's OAuth dance has navigated the popup back to us.
  const delivered = await deliverRedirectResult();
  if (delivered) return;

  // Initial entry: validate query params and start the redirect.
  const cfg = getBridgeConfig();
  const result = parseAndValidateParams(window.location.search, cfg.env);
  if (!result.ok) {
    // We can't postMessage anywhere we trust. Just close.
    window.close();
    return;
  }
  const params = result.params;

  // Persist the parent origin (and tenantId) so we can deliver back to the
  // right place after the redirect round-trip wipes our query params.
  const ctx: BridgeContext = {
    parentOrigin: params.parentOrigin,
    tenantId: params.tenantId ?? null,
  };
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(ctx));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[auth-bridge] sessionStorage write failed', err);
    failClosed(params.parentOrigin);
    return;
  }

  const auth = getBridgeAuth();
  if (params.tenantId) {
    auth.tenantId = params.tenantId;
  }

  try {
    const provider = buildProvider(params);
    // signInWithRedirect navigates THIS window to the OAuth provider. Code
    // after this line does not execute — control returns when Google
    // redirects back here, at which point deliverRedirectResult() picks up.
    await signInWithRedirect(auth, provider);
  } catch (err: any) {
    // eslint-disable-next-line no-console
    console.error('[auth-bridge] signInWithRedirect failed', err);
    failClosed(params.parentOrigin);
  }
}

void runBridge();
